import { Viewer2DV2 } from '@/next/Viewer2DHandle/Viewer2DV2';
import { updatePhotoUrls, useViewer } from '@/stores/viewer';
import { cn } from '@/utils/classname';
import { Progress, toast } from '@skand/ui';
import { useEffect, useState } from 'react';
import { Overlay } from './Overlay';

export const Viewer2D = () => {
  const targetPhoto = useViewer(state => state.targetPhoto);
  const enabled2D = useViewer(state => state.enabled2D);
  const targetAnnotation2D = useViewer(state => state.targetAnnotation2D);
  const annotationGroups = useViewer(state => state.annotationGroups);
  const visibleAnnotations = useViewer(state => state.visibleAnnotations);
  const visiblePredictions = useViewer(state => state.visiblePredictions);
  const predictions = useViewer(state => state.predictions);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [targetReady, setTargetReady] = useState(false);
  const viewer2DHandle = useViewer(state => state.viewer2DHandle);
  const api2D = viewer2DHandle.api;

  // Listen for changes in the target photo
  useEffect(() => {
    if (targetPhoto === null || !enabled2D) return;

    // Show photo overlay
    const showTarget = async () => {
      setTargetReady(false);
      try {
        await updatePhotoUrls(targetPhoto);
        if (targetPhoto.thumbnailUrl && targetPhoto.tileset) {
          await api2D.editor.setPhoto(
            {
              thumbnailUrl: targetPhoto.thumbnailUrl,
              rows: targetPhoto.tileset.rows,
              cols: targetPhoto.tileset.cols,
              getTileURL: (row: number, col: number) =>
                targetPhoto.tileset?.tileURLs.get(`${row} ${col}`) ?? '',
            },
            setDownloadProgress,
          );
        } else if (targetPhoto.url) {
          await api2D.editor.setPhoto(targetPhoto.url, setDownloadProgress);
        }
      } catch (error) {
        toast({ type: 'warn', message: error as string, lifespan: 10000 });
      }
      setTargetReady(true);
    };

    showTarget();
  }, [api2D, targetPhoto, enabled2D]);

  // Render annotations
  useEffect(() => {
    api2D?.editor.clear();
    for (const group of annotationGroups) {
      for (const annotation of group.annotations) {
        if (annotation.sketch2D) {
          if (
            annotation.photo?.id === targetPhoto?.id &&
            visibleAnnotations.has(annotation.id) &&
            targetAnnotation2D !== annotation
          ) {
            api2D?.editor.show(annotation.sketch2D);
          } else {
            api2D?.editor.hide(annotation.sketch2D);
          }
        }
      }
    }
    for (const prediction of predictions) {
      if (targetPhoto?.id === prediction.photoId && visiblePredictions.has(prediction.uniqueId)) {
        api2D?.editor.show(prediction.sketch2D);
        prediction.sketch3D?.show();
      }
    }
  }, [
    annotationGroups,
    api2D,
    targetAnnotation2D,
    targetPhoto,
    visibleAnnotations,
    predictions,
    visiblePredictions,
  ]);

  return (
    <div className={cn('h-full', 'w-full', 'flex', 'justify-center', 'items-center')}>
      <Overlay />
      {!targetReady && (
        <div
          className={cn(
            'h-full',
            'w-full',
            'flex',
            'flex-col',
            'items-center',
            'justify-center',
            'z-1',
            'pointer-events-none',
          )}
        >
          <p className={cn('color-neutral-200', 'typo-text-small')}>
            {downloadProgress > 0 && downloadProgress < 1
              ? 'Loading…'
              : 'Select an image to view here'}
          </p>

          {downloadProgress > 0 && downloadProgress < 1 && (
            <div className="p-t-4">
              <Progress className="w-153px" progress={downloadProgress} />
            </div>
          )}
        </div>
      )}

      <Viewer2DV2 viewer2DHandle={viewer2DHandle} />
    </div>
  );
};
